import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import {Button, Theme} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'
import {makeStyles} from 'tss-react/mui'

const useStyles = makeStyles()((theme: Theme) => ({
  annotationField: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
    width: '100%'
  },
  annotationContent: {
    flex: 1,
    minWidth: 0
  },
  annotationLabel: {
    fontSize: '12px',
    lineHeight: '16px',
    color: '#52667a',
    fontWeight: 400,
    marginBottom: theme.spacing(0.5)
  },
  annotationText: {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 500,
    color: '#29333d',
    wordBreak: 'break-word'
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    color: '#016ad4',
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: 'rgba(1, 106, 212, 0.04)'
    }
  },
  editIcon: {
    fontSize: '16px',
    color: '#016ad4'
  },
  addAnnotationButton: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '16px',
    padding: '12px 16px'
  }
}))

interface AnnotationDisplayProps {
  showAnnotations: boolean
  canManageAnnotations: boolean
  hasNote: boolean
  noteContent?: string
  onAdd: () => void
  onEdit: () => void
}

/**
 * Renders the annotation section in the delivery details view.
 * Shows either an "Add Annotation" button or the existing annotation with an edit button.
 */
export const AnnotationDisplay: React.FC<AnnotationDisplayProps> = ({
  showAnnotations,
  canManageAnnotations,
  hasNote,
  noteContent,
  onAdd,
  onEdit
}) => {
  const {classes} = useStyles()
  const {t} = useTranslation()

  if (canManageAnnotations && !hasNote) {
    return (
      <Button
        fullWidth
        variant="contained"
        color="primary"
        className={classes.addAnnotationButton}
        startIcon={<InsertDriveFileOutlinedIcon />}
        onClick={onAdd}
        data-test-id="add-annotation-button"
      >
        {t('addAnnotation')}
      </Button>
    )
  }

  if (showAnnotations && hasNote) {
    return (
      <div className={classes.annotationField}>
        <div className={classes.annotationContent}>
          <div className={classes.annotationLabel}>{t('annotation')}</div>
          <div className={classes.annotationText}>{noteContent}</div>
        </div>
        {canManageAnnotations && (
          <button
            className={classes.editButton}
            onClick={onEdit}
            data-test-id="edit-annotation-button"
          >
            <EditOutlinedIcon className={classes.editIcon} />
            {t('edit')}
          </button>
        )}
      </div>
    )
  }

  return null
}
